import { Component, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { CalendarOutlineTemplate } from '../../netgen/CalendarOutlineTemplate';
import { Project } from '../../netgen/Project';
import { EditCalendarOutlineTemplateTableComponent } from '../edit-calendar-outline-template-table/edit-calendar-outline-template-table.component';
import { Objects } from 'src/modules/utils/shared/objects';
import { CalendarEventTemplate } from '../../netgen/CalendarEventTemplate';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { ProjectType } from '../../netgen/ProjectType';
import { ProjectTemplate } from '../../netgen/ProjectTemplate';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { ProjectCustomFieldDefinition } from '../../netgen/ProjectCustomFieldDefinition';
import { EnaioProject } from '../../netgen/EnaioProject';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ProjectField } from '../../netgen/ProjectField';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { CreateProjectDto } from '../../netgen/CreateProjectDto';
import { CrudWithPreviewDto } from '../../netgen/CrudWithPreviewDto';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { Strings } from 'src/modules/utils/shared/strings';

@Component({
    selector: 'app-edit-project',
    templateUrl: 'edit-project.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditProjectComponent extends AdminBaseEditComponent<Project> {

    service = inject(ProjectPlanningService);

    definition = ProjectPlanningTools.definitionProject;

    projectTypes: ProjectType[];
    projectFields: ProjectCustomFieldDefinition[];
    projectTemplates: ProjectTemplate[];
    outlineTemplates: CalendarOutlineTemplate[];
    fullOutlineTemplates : CalendarOutlineTemplate[] = [];
    enaioProjects: EnaioProject[];
    users: DatabaseUser[];

    projectTemplate: ProjectTemplate;

    eventTemplates: CalendarEventTemplate[];
    existingEvents: CalendarEvent[] = [];
    eventIdsToDelete: number[] = [];

    selectedOutlineTemplates: CalendarOutlineTemplate[] = [];

    @ViewChild("table")
    table: EditCalendarOutlineTemplateTableComponent;

    getForm(): FrontendForm {
        return this.app.tForm("entity.Project.fields", new FrontendForm([
            this.isInsert() ? new FrontendField("template", this.app.t("entity.Project.other.template"), FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.projectTemplates, item => [item.id, item.title], true),
                dropdownCanFilter: true,
                onValueChanged: this.projectTemplateChanged.bind(this)
            }) : null,
            this.isInsert() ? 
            new FrontendField("projectNumber", "", FrontendFieldType.dropdown, { 
                mandatory: true,
                maxLength: 14, 
                guiGroup: "projectNumber", 
                guiWidthInGroup: 5, 
                dropdownEditable: true,
                dropdownAutoComplete: true,
                dropdownAutoCompleteMaxItems: 100, 
                dropdownAutoCompleteMinLength: 3,
                listItems: FrontendFieldListItem.make(this.enaioProjects, item => [item.projectNumber, item.projectNumber, { html: true, labelInDropdown: item.getHtmlTitle(true)}]),
                onValueChanged: this.projectNumberChanged.bind(this)
            }) : 
            new FrontendField("projectNumber", "", FrontendFieldType.string, { 
                mandatory: true,
                maxLength: 14, 
                guiGroup: "projectNumber", 
                guiWidthInGroup: 8
            }),
            new FrontendField("typeId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "projectNumber", 
                guiWidthInGroup: 4,
                listItems: FrontendFieldListItem.make(this.projectTypes, item => [item.id, item.title], true),
                dropdownCanFilter: true
            }),
            new FrontendField("title", "", FrontendFieldType.string, { 
                mandatory: true,
                guiGroup: "title", 
                guiWidthInGroup: 8                 
            }),
            new FrontendField("contactPersonId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "title", 
                guiWidthInGroup: 4,
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true),
                dropdownCanFilter: true,
                emptyValues: [null, 0]
            }),
            new FrontendField("startDate", "", FrontendFieldType.date, { 
                mandatory: true, 
                datePickerUseDateOnly: true,
                guiGroup: "deliveryDate"
            }),
            new FrontendField("deliveryDate1", "", FrontendFieldType.date, { 
                datePickerUseDateOnly: true,
                guiGroup: "deliveryDate"
            }),
            new FrontendField("deliveryDate2", "", FrontendFieldType.date, { 
                datePickerUseDateOnly: true,
                guiGroup: "deliveryDate"
            }),
            new FrontendField("deliveryDate3", "", FrontendFieldType.date, { 
                datePickerUseDateOnly: true,
                guiGroup: "deliveryDate"
            }),
            new FrontendField("_fields", this.app.t("entity.Project.other.customFields"), FrontendFieldType.table, {
                tableColumns: [
                    new TableColumn("use", this.app.t("entity.Project.other.useField"), TableCellType.bool, 1, { 
                        editable: true,
                        editableCallback: row => !(row.row.raw.mustUse as boolean)
                    }),
                    new TableColumn("key", this.app.t("entity.Project.other.field"), TableCellType.string, 30),
                    new TableColumn("value", this.app.t("entity.ProjectField.fields.value"), TableCellType.string, 30, {
                        editable: true,
                        editableCallback: row => row.row.raw.use as boolean,
                        editor: params => {
                            let def = params.row.raw.field as ProjectCustomFieldDefinition;
                            if (def?.dropdownField) {
                                return new FrontendField("field", this.app.t("entity.Project.other.field"), FrontendFieldType.dropdown, {
                                    listItems: FrontendFieldListItem.make(def.dropdownOptions)
                                });
                            }
                            else {
                                return null;
                            }
                        },
                        getStyleClassCallback: row =>
                                        (row.row.raw.use as boolean)
                                            ? "" : "xbg-gray-200"
                    })
                ],
                tableCanAddRow: false,
                tableCanRemoveRow: false
            }),
            new FrontendField("description", "", FrontendFieldType.textArea, { fixedHeight: "100px"}),
            new FrontendField("events", this.isInsert() ? this.app.t("entity.Project.other.eventsToCreate") : this.app.t("entity.Project.other.eventsToCreateExistingHidden"), FrontendFieldType.string, {
                hasControl: false
            })                
        ]));
    }

    async initAdditionalData(): Promise<void> {
        this.projectTypes = await this.service.getProjectTypes();
        this.projectFields = await this.service.getProjectCustomFieldDefinitions();
        this.projectTemplates = await this.service.getProjectTemplates();
        try {
            this.enaioProjects = await this.service.getEnaioProjects();
        }
        catch {
            this.enaioProjects = [];
        }
        this.outlineTemplates = Arrays.sortBy(await ProjectPlanningTools.definitionCalendarOutlineTemplate.loadDataHandlerList(this.app, this), t => t.title);
        this.eventTemplates = await this.service.getEventTemplates();  
        this.users = await this.service.getActiveUsers();
    }

    async fillForm(): Promise<void> {
        await super.fillForm();
        this.existingEvents = this.isInsert() ? [] : (await this.service.getProjectDto(this.item.id)).events ?? [];
        this.eventIdsToDelete = [];
        this.form.setValue("_fields", this.projectFields.map(field => ({
            mustUse: false,
            use: this.item.customFields?.some(f => f.fieldDefinitionId == field.id),
            key: field.title,
            value: this.item.customFields?.find(f => f.fieldDefinitionId == field.id)?.value ?? "",
            field
        })));
        this.table.beforeDeletePath = this.beforeDeletePath.bind(this);
        this.table.init(Objects.fromPlain(CalendarOutlineTemplate, {
            structurePaths: this.item.structurePaths
        }), this.eventTemplates, await this.service.getOutlineLevels(), await this.service.getOutlineTemplates(), await this.service.getEventTypes(), await this.service.getRoles(), true, [...this.item.structurePaths]);        
    }

    async beforeDeletePath(path: string): Promise<boolean> {
        let events = this.existingEvents.filter(event => path == "" || event.structurePath == path || event.structurePath.startsWith(path + "|"));
        if (events.length == 0) {
            return true;
        }

        let eventsWithoutPermission = events.filter(event => !event.isDeleteAllowed(this.service.getCurrentUser()));
        if (eventsWithoutPermission.length > 0) {
            await this.app.dialog.info(this.app.t("entity.Project.other.deleteOutlineTasksNotAllowed", {
                tasks: this.getEventListHtml(eventsWithoutPermission)
            }), this.app.t("general.error"));
            return false;
        }

        if (!await this.app.dialog.yesNo(this.app.t("entity.Project.other.deleteOutlineTasksConfirmation", {
            count: events.length,
            tasks: this.getEventListHtml(events)
        }), this.app.t("general.warning"))) {
            return false;
        }

        this.eventIdsToDelete = Arrays.getUnique([...this.eventIdsToDelete, ...events.map(event => event.id)]);
        return true;
    }

    getEventListHtml(events: CalendarEvent[]): string {
        return "<ul>" + events.map(event => "<li>" + Strings.escapeHtml(event.title ?? "") + "</li>").join("") + "</ul>";
    }

    async fromForm(): Promise<void> {
        await super.fromForm();
        let fields: ProjectField[] = [];
        for (let row of this.form.getValue("_fields") as any[]) {
            if (row.use) {
                let id = (row.field as ProjectField).id;
                let field = this.item.customFields?.find(f => f.fieldDefinitionId == id);
                if (field == null) {
                    field = Objects.fromPlain(ProjectField, {
                        fieldDefinitionId: id
                    });
                }
                field.value = row.value;
                fields.push(field);
            }
        }
        this.item.customFields = fields;
    }

  /*  async upsert(item: Project): Promise<boolean> {
        item.structurePaths = this.table.getStructurePaths();
        return (await this.app.saveDataHandler(async () => RestEndpoint.main().body(Objects.fromPlain(CreateProjectDto, {
            project: item, 
            events: (this.table.getSelectedEvents() ?? []).map(e => Objects.cloneDeep(e).moveTemplateDataToLocal())
        })).method(this.isInsert() ? "post" : "put").run(this.definition.apiEndpoint).getText(), this, true, !this.multiInsert))[0];
    }
*/

    async upsert(item: Project): Promise<boolean> {
        item.structurePaths = this.table.getStructurePaths();
        return (await this.app.saveDataHandler2(new SaveDataHandler<boolean>({
            componentView: this,
            worker: async () => {
                let body = Objects.fromPlain(CreateProjectDto, {
                    project: item, 
                    events: (this.table.getSelectedEvents() ?? []).map(e => Objects.cloneDeep(e).moveTemplateDataToLocal()),
                    eventIdsToDelete: this.eventIdsToDelete.filter(id => !item.structurePaths.includes(this.existingEvents.find(event => event.id == id)?.structurePath))
                });

                let result = await RestEndpoint.main().body(body).query({ previewIfNeeded: true }).method(this.isInsert() ? "post" : "put").run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                if (!result.executed) {
                    if (!await this.app.dialog.yesNo(this.app.t("entity.Project.other.previewWarning", { previewItems: Arrays.itemsToString(result.previewItems, "<br>") }), this.app.t("general.warning"))) {
                        return false;
                    }
                    await RestEndpoint.main().body(body).method(this.isInsert() ? "post" : "put").run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                }

                if (this.isInsert()) {
                    await this.service.getProjects(true);
                }
                await this.app.trackChanges();
                return true;
            },
            navigateBack: !this.multiInsert,    
            isSuccess: result => result
        }))).data;
    }

    validateForm(): boolean {
        return this.app.validateForm(this.form, this.table != null ? () => this.table.validate() : null);
    }

    getDialogButtons(): ButtonDefinition[] {
        return [
            ...super.getDialogButtons(),
            !this.isInsert() && !this.item.finished ? new ButtonDefinition("finish", this.app.t("entity.Project.other.finishButton"), true, "fas fa-check", "", "warn") : null,
            !this.isInsert() && this.item.finished ? new ButtonDefinition("open", this.app.t("entity.Project.other.reopenButton"), true, "fas fa-check", "", "warn") : null,
            !this.isInsert() &&  this.item.isDeleteAllowed(this.service.getCurrentUser()) ? new ButtonDefinition("delete", this.app.t("general.delete"), true, "fas fa-trash", "", "danger") : null
        ];
    }

    async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "delete") {
            if (!await this.app.dialog.yesNo(this.app.t("entity.Project.other.deleteConfirmation"), this.app.t("entity.Project.other.deleteTitle"))) {
                return;
            }
            await ProjectPlanningTools.definitionProject.deleteDataHandler(this.item.id, this.app, this);
            await this.app.trackChanges();
            await this.close();
        }
        else if (id == "open") {
            this.form.get(this.item);
            if (this.service.reopenProject(this.item)) {
                this.form.fill(this.item);
                await this.save(id);
            }
        }
        else if (id == "finish") {
            if (!await this.app.dialog.yesNo(this.app.t("entity.Project.other.finishConfirmation"), this.app.t("entity.Project.other.finishTitle"))) {
                return;
            }
            this.form.get(this.item);
            if (this.service.finishProject(this.item, this.service.getCurrentUser())) {
                this.form.fill(this.item);
                await this.save(id);
            }
        }
        else {
            await super.buttonClickedFromExtern(id);
        }
    }

    async projectTemplateChanged(): Promise<void> {
        let templateId = this.form.getValueNumber("template");
        if (!(templateId > 0)) {
            return;
        }
        let template = await ProjectPlanningTools.definitionProjectTemplate.loadDataHandlerGet(templateId, this.app, this);
        this.form.setValue("title", template.title); 
        this.form.setValue("typeId", template.typeId);      
        this.form.setValue("description", template.description);    

        let rows = this.form.getValue("_fields") as any[];
        for (let row of rows) {
            let field = template.fields?.find(f => f.fieldDefinitionId == (row.field as ProjectCustomFieldDefinition).id);
            row.value = field?.value ?? row.value;
            row.mustUse = field != null;
            row.use = field != null;
        }
        this.form.setValue("_fields", [...rows]);
        this.projectTemplate = Objects.cloneDeep(template);
    }

    projectNumberChanged(): void {
        let projectNumber = this.form.getValue("projectNumber");
        let enaioProject = this.enaioProjects.find(p => p.projectNumber == projectNumber);
        if (enaioProject != null) {
            this.form.setValue("title", enaioProject.title);
        } 
    }

}
